import React from 'react';
import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';

const fmt = (n) => (n != null ? Number(n).toLocaleString('en-GB') : 'N/A');

const S = {
  table: { width: '100%', borderCollapse: 'collapse', margin: '0 0 16px 0', fontSize: '0.9em', background: '#fff', border: '1px solid #dfe4ea' },
  th: { padding: '8px', border: '1px solid #dfe4ea', backgroundColor: '#f3f3f5', textAlign: 'left' },
  thR: { padding: '8px', border: '1px solid #dfe4ea', backgroundColor: '#f3f3f5', textAlign: 'right' },
  td: { padding: '8px', border: '1px solid #dfe4ea', verticalAlign: 'top' },
  tdR: { padding: '8px', border: '1px solid #dfe4ea', verticalAlign: 'top', textAlign: 'right' },
  alt: { backgroundColor: '#fafafa' },
};

const E1Rows = ({ rows, label }) => {
  if (!rows?.length) {
    return <tr><td colSpan={8} style={{ ...S.td, color: '#6c757d', fontStyle: 'italic' }}>No data for {label}</td></tr>;
  }
  return rows.map((r, i) => (
    <tr key={i} style={i % 2 !== 0 ? S.alt : {}}>
      {i === 0 && <td rowSpan={rows.length} style={{ ...S.td, fontWeight: 'bold', verticalAlign: 'top' }}>{label}</td>}
      <td style={S.td}>{r.scope}</td>
      <td style={S.td}>{r.entityName}</td>
      <td style={S.td}>{r.epsName}</td>
      <td style={S.tdR}>{fmt(r.contribution)}</td>
      <td style={S.td}>{r.unit}</td>
      <td style={S.td}>{r.emissionFactorSource ?? 'N/A'}</td>
      <td style={S.tdR}>{fmt(r.co2Et)}</td>
    </tr>
  ));
};

const SimpleRows = ({ rows, label, renderCells, colSpan }) => {
  if (!rows?.length) {
    return <tr><td colSpan={colSpan} style={{ ...S.td, color: '#6c757d', fontStyle: 'italic' }}>No data for {label}</td></tr>;
  }
  return rows.map((r, i) => (
    <tr key={i} style={i % 2 !== 0 ? S.alt : {}}>
      {i === 0 && <td rowSpan={rows.length} style={{ ...S.td, fontWeight: 'bold', verticalAlign: 'top' }}>{label}</td>}
      {renderCells(r)}
    </tr>
  ));
};

const OrgBoundaryTree = ({ nodes }) => {
  if (!nodes?.length) return <p className="text-muted small">No entity hierarchy data available.</p>;

  const nm = new Map(nodes.map((n) => [n.id, { ...n, children: [] }]));
  const roots = [];
  for (const n of nm.values()) {
    if (n.parentId === null || !nm.has(n.parentId)) roots.push(n);
    else nm.get(n.parentId).children.push(n);
  }

  const renderNode = (node, depth = 0) => (
    <React.Fragment key={node.id}>
      <tr>
        <td style={{ ...S.td, paddingLeft: `${depth * 20 + 8}px` }}>{node.name}</td>
        <td style={{ ...S.td, color: '#6c757d' }}>{node.level}</td>
      </tr>
      {node.children.map((child) => renderNode(child, depth + 1))}
    </React.Fragment>
  );

  return (
    <table style={S.table}>
      <thead>
        <tr><th style={S.th}>Entity</th><th style={S.th}>Level</th></tr>
      </thead>
      <tbody>{roots.map((r) => renderNode(r))}</tbody>
    </table>
  );
};

const CsrdAppendixTables = ({ value }) => {
  let appendix = null;
  try {
    if (value) appendix = JSON.parse(value);
  } catch {
    return <p className="text-muted small">Invalid appendix data.</p>;
  }
  if (!appendix) return <p className="text-muted small">No appendix data available.</p>;

  return (
    <div style={{ paddingTop: '8px' }}>
      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>ESRS E1 – Climate</h6>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Year</th>
            <th style={S.th}>Scope</th>
            <th style={S.th}>Entity</th>
            <th style={S.th}>Activity Category</th>
            <th style={S.thR}>Contribution</th>
            <th style={S.th}>Unit</th>
            <th style={S.th}>Emission Factor Source</th>
            <th style={S.thR}>CO₂e (t)</th>
          </tr>
        </thead>
        <tbody>
          <E1Rows rows={appendix.e1ReportYear} label="Report Year" />
          <E1Rows rows={appendix.e1BaseYear} label="Base Year" />
        </tbody>
      </table>

      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>ESRS E2 – Pollution</h6>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Year</th><th style={S.th}>Entity</th><th style={S.th}>Fuel</th>
            <th style={S.thR}>Amount Combusted</th><th style={S.th}>Unit</th>
          </tr>
        </thead>
        <tbody>
          <SimpleRows rows={appendix.e2ReportYear} label="Report Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.fuelName}</td><td style={S.tdR}>{fmt(r.contribution)}</td><td style={S.td}>{r.unit}</td></>)} />
          <SimpleRows rows={appendix.e2BaseYear} label="Base Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.fuelName}</td><td style={S.tdR}>{fmt(r.contribution)}</td><td style={S.td}>{r.unit}</td></>)} />
        </tbody>
      </table>

      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>ESRS E3 – Water</h6>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Year</th><th style={S.th}>Entity</th><th style={S.th}>Water Type</th>
            <th style={S.thR}>Volume</th><th style={S.th}>Unit</th>
          </tr>
        </thead>
        <tbody>
          <SimpleRows rows={appendix.e3ReportYear} label="Report Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.waterType}</td><td style={S.tdR}>{fmt(r.volume)}</td><td style={S.td}>{r.unit}</td></>)} />
          <SimpleRows rows={appendix.e3BaseYear} label="Base Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.waterType}</td><td style={S.tdR}>{fmt(r.volume)}</td><td style={S.td}>{r.unit}</td></>)} />
        </tbody>
      </table>

      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>ESRS E5 – Resource Use &amp; Circular Economy</h6>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Year</th><th style={S.th}>Entity</th><th style={S.th}>Waste Type</th>
            <th style={S.thR}>Contribution</th><th style={S.th}>Unit</th>
          </tr>
        </thead>
        <tbody>
          <SimpleRows rows={appendix.e5ReportYear} label="Report Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.wasteType}</td><td style={S.tdR}>{fmt(r.contribution)}</td><td style={S.td}>{r.unit}</td></>)} />
          <SimpleRows rows={appendix.e5BaseYear} label="Base Year" colSpan={5}
            renderCells={(r) => (<><td style={S.td}>{r.entityName}</td><td style={S.td}>{r.wasteType}</td><td style={S.tdR}>{fmt(r.contribution)}</td><td style={S.td}>{r.unit}</td></>)} />
        </tbody>
      </table>

      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Organisational Boundary</h6>
      <OrgBoundaryTree nodes={appendix.orgBoundary} />

      <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Site Inventory</h6>
      {appendix.siteInventory?.length ? (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Site</th><th style={S.th}>Description</th>
              <th style={S.th}>Activities</th><th style={S.th}>Assets</th>
            </tr>
          </thead>
          <tbody>
            {appendix.siteInventory.map((s, i) => (
              <tr key={i} style={i % 2 !== 0 ? S.alt : {}}>
                <td style={S.td}>{s.entityName}</td>
                <td style={S.td}>{s.desc ?? ''}</td>
                <td style={S.td}>{s.epsNames}</td>
                <td style={S.td}>{s.assetNames}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-muted small">No site inventory data available.</p>
      )}
    </div>
  );
};

const COMPLEX_FIELD_IDS = new Set(['csrd-appendix-tables', 'intensity-measures']);

const IntensityMeasuresTable = ({ value }) => {
  let measures = null;
  try {
    if (value) measures = JSON.parse(value);
  } catch {
    return <p className="text-muted small">Invalid intensity data.</p>;
  }
  if (!measures?.length) return <p className="text-muted small">No intensity data available.</p>;

  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Business Measure</th>
          <th style={S.th}>Unit</th>
          <th style={S.thR}>Report Year</th>
          <th style={S.thR}>Base Year</th>
        </tr>
      </thead>
      <tbody>
        {measures.map((m, i) => (
          <tr key={i} style={i % 2 === 0 ? S.alt : {}}>
            <td style={S.td}>Emission intensity ({m.name})</td>
            <td style={S.td}>tCO₂e / {m.name}</td>
            <td style={S.tdR}>{m.reportIntensity !== null ? fmt(m.reportIntensity) : 'N/A'}</td>
            <td style={S.tdR}>{m.baseIntensity !== null ? fmt(m.baseIntensity) : 'N/A'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SystemDataTable = ({ fields }) => {
  if (!fields || fields.length === 0) {
    return <p className="text-muted small mb-0">No CarbonView system data is required for this section.</p>;
  }

  const simpleFields = fields.filter((f) => !COMPLEX_FIELD_IDS.has(f.id));
  const appendixField = fields.find((f) => f.id === 'csrd-appendix-tables');
  const intensityField = fields.find((f) => f.id === 'intensity-measures');

  return (
    <div>
      {simpleFields.length > 0 && (
        <Table responsive hover size="sm" className="mb-0 narrative-data-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Value</th>
              <th className="text-center">Source</th>
            </tr>
          </thead>
          <tbody>
            {simpleFields.map((field) => (
              <tr key={field.id}>
                <td>{field.name}</td>
                <td className="text-right">
                  <strong>{typeof field.value === 'number' ? field.value.toLocaleString('en-GB') : field.value}</strong>
                  {field.unit && <span className="text-muted ml-1">{field.unit}</span>}
                </td>
                <td className="text-center">
                  <OverlayTrigger
                    placement="left"
                    overlay={(
                      <Tooltip id={`${field.id}-source`}>
                        {field.quality.source || 'CarbonView'}
                        {field.quality.timestamp ? ` • ${new Date(field.quality.timestamp).toLocaleString()}` : ''}
                      </Tooltip>
                    )}
                  >
                    <span className="narrative-source-icon">
                      CarbonView <IoInformationCircleOutline />
                    </span>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {intensityField && (
        <div className="px-3 pb-3">
          <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
            <strong>{intensityField.name}</strong>
            <OverlayTrigger
              placement="left"
              overlay={(
                <Tooltip id="intensity-measures-source">
                  {intensityField.quality?.source || 'CarbonView'}
                  {intensityField.quality?.timestamp ? ` • ${new Date(intensityField.quality.timestamp).toLocaleString()}` : ''}
                </Tooltip>
              )}
            >
              <span className="narrative-source-icon text-muted small">
                CarbonView <IoInformationCircleOutline />
              </span>
            </OverlayTrigger>
          </div>
          <IntensityMeasuresTable value={intensityField.value} />
        </div>
      )}

      {appendixField && (
        <div className="px-3 pb-3">
          <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
            <strong>{appendixField.name}</strong>
            <OverlayTrigger
              placement="left"
              overlay={(
                <Tooltip id="csrd-appendix-source">
                  {appendixField.quality?.source || 'CarbonView'}
                  {appendixField.quality?.timestamp ? ` • ${new Date(appendixField.quality.timestamp).toLocaleString()}` : ''}
                </Tooltip>
              )}
            >
              <span className="narrative-source-icon text-muted small">
                CarbonView <IoInformationCircleOutline />
              </span>
            </OverlayTrigger>
          </div>
          <CsrdAppendixTables value={appendixField.value} />
        </div>
      )}
    </div>
  );
};

export default SystemDataTable;
